import { Component, EventEmitter, Output, signal, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';
import { CustomPopoverService } from '../../../shared/services/custom-popover.service';
import { CustomModalController } from '../../../shared/services/custom-modal-controller.service';
import { ArticleModalComponent } from '../article-modal/article-modal.component';
import { ArticleComponentData } from '../../../types/material.interface';
import { isDesktopViewport } from '../../../shared/utils/viewport.utils';
import { StorageService, STORAGE_KEYS } from '../../../shared/services/storage.service';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { ScreenLayer } from '../../../shared/enums/screen-layer.enum';
import { ModalDomain } from '../../../shared/enums/modal-domain.enum';

@Component({
  selector: 'app-tyc',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, LanguageSelectorComponent],
  templateUrl: './tyc.component.html',
  styleUrls: ['./tyc.component.scss']
})
export class TycComponent {
  private platformId = inject(PLATFORM_ID);
  private customPopoverService = inject(CustomPopoverService);
  private modalCtrl = inject(CustomModalController);
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private tService = inject(TranslationService);

  @Output() accepted = new EventEmitter<{ terms: boolean; cookies: boolean }>();
  @Output() privacyLinkClicked = new EventEmitter<void>();

  // Checkbox states
  termsAccepted = signal(false);
  cookiesAccepted = signal(false);

  // Expose isDesktopViewport signal
  isDesktopViewport = isDesktopViewport;

  constructor() {
    this.loadSavedState();
  }

  private loadSavedState(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const saved = this.storageService.get<{ terms?: boolean; cookies?: boolean } | null>('ui', STORAGE_KEYS.ui.tycAccepted, null);
        if (saved) {
          this.termsAccepted.set(saved.terms ?? false);
          this.cookiesAccepted.set(saved.cookies ?? false);
        }
      } catch {
        // Ignore parse errors
      }
    }
  }

  private saveState(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.storageService.set('ui', STORAGE_KEYS.ui.tycAccepted, {
        terms: this.termsAccepted(),
        cookies: this.cookiesAccepted(),
        acceptedAt: new Date().toISOString()
      });
    }
  }

  toggleTerms(): void {
    this.termsAccepted.set(!this.termsAccepted());
  }

  toggleCookies(): void {
    this.cookiesAccepted.set(!this.cookiesAccepted());
  }

  canContinue(): boolean {
    return this.termsAccepted();
  }

  async onContinue(): Promise<void> {
    if (this.canContinue()) {
      this.saveState();
      this.accepted.emit({
        terms: this.termsAccepted(),
        cookies: this.cookiesAccepted()
      });
      
      // Use custom popover service to dismiss with animation
      await this.customPopoverService.dismiss({
        accepted: true,
        terms: this.termsAccepted(),
        cookies: this.cookiesAccepted()
      }, 'accepted');
    }
  }

  onPrivacyLinkClick(): void {
    this.privacyLinkClicked.emit();
    this.openTycModal();
  }

  onTermsClick(event: Event): void {
    event.stopPropagation(); // Prevent checkbox toggle
    this.openTycModal();
  }

  onPrivacyClick(event: Event): void {
    event.stopPropagation(); // Prevent checkbox toggle
    this.privacyLinkClicked.emit();
    this.openTycModal();
  }

  private async openTycModal(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      // Load the tyc.md content
      const markdownContent = await this.http.get('assets/data/tyc.md', { responseType: 'text' }).toPromise();
      
      // Create ArticleComponentData array with the markdown content
      const articleContent: ArticleComponentData[] = [
        { type: 'markdown', data: markdownContent || '' },
        { type: 'button', data: { text: 'Acepto', action: 'accept' } }
      ];

      const modal = await this.modalCtrl.create({
        component: ArticleModalComponent,
        layer: ScreenLayer.Default,
        modalDomain: ModalDomain.Terms,
        modalId: 'tyc',
        componentProps: {
          title: this.tService.instant('tyc.title'),
          content: articleContent,
          showAcceptButton: false // Just viewing, acceptance is in the TyC popup
        },
        cssClass: ['tyc-markdown-modal', 'remateriar-modal', 'modal-wrapper', 'remateriar-modal-height', 'schedule-entity-modal-class', 'modal-none-backdrop'],
      });

      await modal.present();
    } catch (error) {
      console.error('Error loading TyC content:', error);
    }
  }

  // Static method to check if user has already accepted terms
  // Uses direct localStorage as fallback since static methods can't use DI
  static hasAcceptedTerms(): boolean {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('app:v1:ui:tycAccepted');
        if (raw) {
          const saved = JSON.parse(raw) as { terms?: boolean };
          return saved?.terms === true;
        }
      } catch { /* ignore */ }
    }
    return false;
  }
}
